import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import type { Analysis, CalculatorInputs, CalculatorResults, PriceLevel } from '../lib/types';
import { AUTH_ENABLED } from '../lib/auth';
const noopGetToken = async () => null;

export function useAnalyses(
  propertyId: string | undefined,
  getToken: () => Promise<string | null> = noopGetToken
) {
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalyses = useCallback(async () => {
    if (!AUTH_ENABLED || !propertyId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await apiFetch<Analysis[]>(`/properties/${propertyId}/analyses`, {}, getToken);
      setAnalyses(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [propertyId, getToken]);

  useEffect(() => { fetchAnalyses(); }, [fetchAnalyses]);

  const createAnalysis = useCallback(async (body: {
    askingPrice: number;
    inputs: CalculatorInputs;
    results: CalculatorResults;
    priceLevels: PriceLevel[];
    profileId: string;
  }): Promise<Analysis> => {
    const analysis = await apiFetch<Analysis>(`/properties/${propertyId}/analyses`, {
      method: 'POST',
      body: JSON.stringify(body),
    }, getToken);
    setAnalyses(prev => [analysis, ...prev]); // Newest first
    return analysis;
  }, [propertyId, getToken]);

  const deleteAnalysis = useCallback(async (analysisId: string) => {
    await apiFetch(`/properties/${propertyId}/analyses/${analysisId}`, { method: 'DELETE' }, getToken);
    setAnalyses(prev => prev.filter(a => a.id !== analysisId));
  }, [propertyId, getToken]);

  return { analyses, loading, error, fetchAnalyses, createAnalysis, deleteAnalysis };
}

export function useAnalysisDetail(
  propertyId: string | undefined,
  analysisId: string | undefined,
  getToken: () => Promise<string | null> = noopGetToken
) {
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalysis = useCallback(async () => {
    if (!AUTH_ENABLED || !propertyId || !analysisId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await apiFetch<Analysis>(`/properties/${propertyId}/analyses/${analysisId}`, {}, getToken);
      setAnalysis(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [propertyId, analysisId, getToken]);

  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

  return { analysis, loading, error, refetch: fetchAnalysis };
}
